import { useState } from "react";
import { HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { VOLUNTEER } from "@/constants/testIds";

const HELP_TYPE_OPTIONS = [
  { key: "story", label: "Share my experience/story" },
  { key: "outreach", label: "Community outreach" },
  { key: "listen", label: "Listen/support people affected by bias" },
  { key: "legal", label: "Legal expertise" },
  { key: "mental_health", label: "Mental health/wellness expertise" },
  { key: "business", label: "Business/professional expertise" },
  { key: "tech", label: "Technology/data" },
  { key: "comms", label: "Communications/social media" },
  { key: "fundraising", label: "Fundraising" },
  { key: "organizing", label: "Community organizing" },
  { key: "research", label: "Research/education" },
  { key: "connect", label: "Introduce/connect BiasBuster to people or organizations" },
  { key: "anywhere", label: "Volunteer wherever needed" },
  { key: "other", label: "Other" },
];

export function VolunteerForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [profession, setProfession] = useState("");
  const [helpTypes, setHelpTypes] = useState([]);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");

  const toggleHelpType = (key) => {
    setHelpTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await api.post("/volunteers", {
        name, email,
        phone: phone || undefined,
        location: location || undefined,
        profession: profession || undefined,
        help_types: helpTypes,
        message: message || undefined,
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div id="volunteer-section" data-testid={VOLUNTEER.section} className="bg-zinc-950 text-white rounded-sm p-8 md:p-10">
      <HeartHandshake className="h-8 w-8 text-[#4FD1C5] mb-4" />
      <h3 className="font-display font-bold uppercase text-xl tracking-tight mb-2">Become a Friend of BAP</h3>
      <p className="text-zinc-400 text-sm leading-relaxed mb-6 max-w-xl">
        You don't have to experience racial bias yourself to help change it. Give your time, voice, expertise,
        connections, resources — or simply stand with us. Tell us how you'd like to get involved and we'll
        follow up as this network takes shape.
      </p>
      {status === "done" ? (
        <p data-testid={VOLUNTEER.successMessage} className="text-[#4FD1C5] font-medium text-sm">
          Thank you for offering to get involved — we'll follow up soon.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
              data-testid={VOLUNTEER.nameInput}
              className="bg-white border-zinc-300 text-zinc-950 rounded-none focus:ring-1 focus:ring-[#4FD1C5]"
            />
            <Input
              type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid={VOLUNTEER.emailInput}
              className="bg-white border-zinc-300 text-zinc-950 rounded-none focus:ring-1 focus:ring-[#4FD1C5]"
            />
            <Input
              type="tel" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
              data-testid={VOLUNTEER.phoneInput}
              className="bg-white border-zinc-300 text-zinc-950 rounded-none focus:ring-1 focus:ring-[#4FD1C5]"
            />
            <Input
              placeholder="City, State" value={location} onChange={(e) => setLocation(e.target.value)}
              data-testid={VOLUNTEER.locationInput}
              className="bg-white border-zinc-300 text-zinc-950 rounded-none focus:ring-1 focus:ring-[#4FD1C5]"
            />
            <Input
              placeholder="Profession / organization (optional)" value={profession} onChange={(e) => setProfession(e.target.value)}
              data-testid={VOLUNTEER.professionInput}
              className="bg-white border-zinc-300 text-zinc-950 rounded-none focus:ring-1 focus:ring-[#4FD1C5] sm:col-span-2"
            />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">How would you like to help?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {HELP_TYPE_OPTIONS.map((o) => (
                <label key={o.key} className="flex items-start gap-2.5 text-sm text-zinc-300 cursor-pointer">
                  <Checkbox
                    checked={helpTypes.includes(o.key)}
                    onCheckedChange={() => toggleHelpType(o.key)}
                    data-testid={VOLUNTEER.helpTypeCheckbox(o.key)}
                    className="border-zinc-500 data-[state=checked]:bg-[#4FD1C5] data-[state=checked]:border-[#4FD1C5] data-[state=checked]:text-zinc-950 mt-0.5"
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          <Textarea
            placeholder="Tell us how you'd like to contribute or what you can offer"
            value={message} onChange={(e) => setMessage(e.target.value)}
            data-testid={VOLUNTEER.messageInput}
            className="bg-white border-zinc-300 text-zinc-950 rounded-none focus:ring-1 focus:ring-[#4FD1C5] min-h-[90px]"
          />

          <Button
            type="submit" disabled={status === "loading"} data-testid={VOLUNTEER.submitButton}
            className="bg-[#4FD1C5] text-zinc-950 font-semibold rounded-none hover:bg-white whitespace-nowrap"
          >
            {status === "loading" ? "Sending..." : "Get Involved"}
          </Button>
        </form>
      )}
    </div>
  );
}
